import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { VerificationStatusResponseDto } from '../dto/response/verification-status-response.dto';
import verificationStatusService from '../services/verification-status.service';
import { VerificationStatusSaveRequestDto } from '../dto/request/verification-status-save-request.dto';
import { VerificationStatusUpdateRequestDto } from '../dto/request/verification-status-update-request.dto';
import { showError, showSuccess } from '../../../../utils/Notifications';
import { AxiosError } from 'axios';
import { LocalesLanguagesConstants } from '../../../../locales/locales';

const initialState = {
  status: 'idle',
  verificationStatuses: [] as VerificationStatusResponseDto[] | null,
  verificationStatus: {} as VerificationStatusResponseDto | null,
  error: null as string | null,
};

export const fetchVerificationStatuses = createAsyncThunk(
  'verificationStatuses/fetchVerificationStatuses',
  async () => {
    return await verificationStatusService.fetchVerificationStatuses();
  }
);

export const fetchVerificationStatusById = createAsyncThunk(
  'verificationStatuses/fetchVerificationStatusById',
  async (params: { id: string; language: LocalesLanguagesConstants }) => {
    return await verificationStatusService.fetchVerificationStatusById(
      params.id,
      params.language
    );
  }
);

export const saveVerificationStatus = createAsyncThunk(
  'verificationStatuses/saveVerificationStatus',
  async (params: {
    saveRequest: VerificationStatusSaveRequestDto;
    language: LocalesLanguagesConstants;
  }) => {
    return await verificationStatusService.saveVerificationStatus(
      params.saveRequest,
      params.language
    );
  }
);

export const updateVerificationStatus = createAsyncThunk(
  'verificationStatuses/updateVerificationStatus',
  async (params: {
    updateRequest: VerificationStatusUpdateRequestDto;
    language: LocalesLanguagesConstants;
  }) => {
    return await verificationStatusService.updateVerificationStatus(
      params.updateRequest,
      params.language
    );
  }
);

export const deleteVerificationStatus = createAsyncThunk(
  'verificationStatuses/deleteVerificationStatus',
  async (existingId: string, { rejectWithValue, dispatch }) => {
    try {
      await verificationStatusService.deleteVerificationStatus(existingId);
      dispatch(fetchVerificationStatuses());
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

const verificationStatusSlice = createSlice({
  name: 'verificationStatusSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchVerificationStatuses.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchVerificationStatuses.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.verificationStatuses = action.payload;
      })
      .addCase(fetchVerificationStatuses.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
        showError(
          `Fetching verification status failed. ${action.payload as string}`
        );
      })
      .addCase(fetchVerificationStatusById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchVerificationStatusById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.verificationStatus = action.payload;
      })
      .addCase(fetchVerificationStatusById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
        showError(
          `Fetching verification status failed. ${action.payload as string}`
        );
      })
      .addCase(saveVerificationStatus.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveVerificationStatus.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.verificationStatuses?.push(action.payload);
        showSuccess('Verification status created successfully.');
      })
      .addCase(saveVerificationStatus.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
        showError(
          `Creating verification status failed. ${action.payload as string}`
        );
      })
      .addCase(updateVerificationStatus.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateVerificationStatus.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (state.verificationStatuses) {
          const verificationStatusIndex = state.verificationStatuses.findIndex(
            (verificationStatus) => verificationStatus.id === action.payload.id
          );
          if (verificationStatusIndex !== -1) {
            state.verificationStatuses[verificationStatusIndex] =
              action.payload;
          }
        }
      })
      .addCase(updateVerificationStatus.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
        showError(
          `Updating verification status failed. ${action.payload as string}`
        );
      })
      .addCase(deleteVerificationStatus.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteVerificationStatus.fulfilled, (state) => {
        state.status = 'succeeded';
        showSuccess('Verification status deleted successfully.');
      })
      .addCase(deleteVerificationStatus.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
        showError(
          `Deleting verification status failed. ${action.payload as string}`
        );
      });
  },
});

export default verificationStatusSlice.reducer;
